import { useEffect, useMemo, useRef, useState } from 'react';
import { Text, Spinner, Button, Dropdown, Option, makeStyles, tokens } from '@fluentui/react-components';
import { Play24Regular, Pause24Regular, ArrowCounterclockwise24Regular } from '@fluentui/react-icons';
import { useQuery } from '@tanstack/react-query';
import { fetchDroneFlights, type DroneFlight } from '../api/drone-flights.api';
import { fetchLotesForMap } from '../api/dashboard.api';
import { fetchModules } from '../api/modules.api';
import { MapView, type MapFeature } from '../components/shared/MapView';
import { KpiCard } from '../components/shared/KpiCard';
import { DataTable } from '../components/shared/DataTable';
import { SearchableSelect } from '../components/shared/SearchableSelect';
import { useT } from '../i18n';
import { useIntlFormat } from '../hooks/useIntlFormat';
import { useRanchoStore } from '../store/rancho.store';
import { usePermission } from '../hooks/usePermission';
import { DroneFlightImportDialog } from '../components/shared/DroneFlightImportDialog';
import { buildFlightPath, flightAt, type LatLng } from '../utils/flight-path';
import { parseLoteZoneId } from '../utils/lote-zone-options';
import { onlyCampoLotes } from '../utils/lote-uso';
import { useLoteZoneOptions } from '../hooks/useLoteZoneOptions';
import { PageHeader } from '../components/shared/PageHeader';

/**
 * `/drones` — vuelos de fumigación con dron: indicadores, lista y repetición animada del vuelo
 * elegido sobre el mapa satelital del lote (trayectoria ilustrativa, ver utils/flight-path.ts).
 */

const useStyles = makeStyles({
  root:    { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalL, padding: tokens.spacingVerticalL },
  header:  { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: tokens.spacingHorizontalM, flexWrap: 'wrap' },
  kpis:    { display: 'flex', gap: tokens.spacingHorizontalM, flexWrap: 'wrap' },
  body:    { display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)', gap: tokens.spacingHorizontalL, alignItems: 'start' },
  panel:   { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalS, minWidth: 0 },
  map:     { height: '300px', borderRadius: tokens.borderRadiusMedium, overflow: 'hidden', border: `1px solid ${tokens.colorNeutralStroke2}` },
  controls:{ display: 'flex', alignItems: 'center', gap: tokens.spacingHorizontalS, flexWrap: 'wrap' },
  bar:     { height: '6px', borderRadius: '3px', backgroundColor: tokens.colorNeutralBackground5, overflow: 'hidden' },
  barFill: { height: '100%', backgroundColor: tokens.colorPaletteGreenForeground1 },
  stats:   { display: 'flex', gap: tokens.spacingHorizontalL, flexWrap: 'wrap', fontSize: tokens.fontSizeBase300 },
  note:    { color: tokens.colorNeutralForeground3, fontSize: tokens.fontSizeBase200 },
  tableBox:{ maxHeight: '430px', overflowY: 'auto', border: `1px solid ${tokens.colorNeutralStroke2}`, borderRadius: tokens.borderRadiusMedium },
});

/** Velocidad de la repetición: un vuelo real dura ~6 min, a ×30 se ve en ~12 s. */
const SPEEDS = [15, 30, 60] as const;

const mmss = (sec: number) => `${String(Math.floor(sec / 60)).padStart(2, '0')}:${String(Math.floor(sec % 60)).padStart(2, '0')}`;

export function DronesPage() {
  const s = useStyles();
  const t = useT();
  const { fmtDateTime, fmtNum } = useIntlFormat();
  const { activeRanchoId } = useRanchoStore();

  const flightsQ = useQuery({ queryKey: ['drone-flights', activeRanchoId], queryFn: () => fetchDroneFlights({ ranchoId: activeRanchoId }) });
  const lotesQ = useQuery({ queryKey: ['lotes-map', activeRanchoId], queryFn: () => fetchLotesForMap(activeRanchoId), select: onlyCampoLotes });
  const modulesQ = useQuery({ queryKey: ['modules-rancho', activeRanchoId], queryFn: () => fetchModules({ ranchoId: activeRanchoId }) });
  const [zoneFilter, setZoneFilter] = useState('');
  const [importOpen, setImportOpen] = useState(false);
  const canImport = usePermission('devices:write');

  const applicationFlights = useMemo(
    () => (flightsQ.data ?? []).filter(f => f.flightType === 'APPLICATION').sort((a, b) => b.flightDate.localeCompare(a.flightDate)),
    [flightsQ.data],
  );
  const zoneOptions = useLoteZoneOptions(lotesQ.data ?? [], modulesQ.data ?? []);
  // Filtro del árbol: un lote incluye sus vuelos; una zona o subzona, solo los suyos.
  const flights = useMemo(() => {
    const sel = zoneFilter ? parseLoteZoneId(zoneFilter, modulesQ.data ?? []) : null;
    if (!sel) return applicationFlights;
    return applicationFlights.filter(f => (sel.moduleId ? f.moduleId === sel.moduleId : f.loteId === sel.loteId));
  }, [applicationFlights, zoneFilter, modulesQ.data]);
  const loteById = useMemo(() => new Map((lotesQ.data ?? []).map(l => [l.loteId, l])), [lotesQ.data]);

  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [playing, setPlaying] = useState(true);
  const [speed, setSpeed] = useState<number>(30);
  const [progress, setProgress] = useState(0);

  const selected: DroneFlight | undefined = flights.find(f => f.flightId === selectedId) ?? flights[0];
  const lote = selected ? loteById.get(selected.loteId) : undefined;

  const ring = useMemo<LatLng[]>(
    () => (lote?.boundary?.coordinates?.[0] ?? []).map(([lng, lat]) => [lat, lng] as LatLng),
    [lote],
  );
  const planned = useMemo(
    () => (selected ? buildFlightPath(ring, selected.areaCoveredHa ?? 0, selected.telemetry?.rowSpacingM) : []),
    [ring, selected],
  );

  // Cada vuelo nuevo arranca desde el principio.
  const selectedKey = selected?.flightId;
  useEffect(() => { setProgress(0); setPlaying(true); }, [selectedKey]);

  // Avance: la repetición dura (duración real ÷ velocidad) segundos.
  const rafRef = useRef(0);
  useEffect(() => {
    if (!playing || !selected) return;
    const totalMs = ((selected.durationSec ?? 360) / speed) * 1000;
    let last = performance.now();
    const tick = (now: number) => {
      const dt = now - last; last = now;
      setProgress(p => Math.min(1, p + dt / totalMs));
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafRef.current);
  }, [playing, selected, speed]);
  useEffect(() => { if (progress >= 1 && playing) setPlaying(false); }, [progress, playing]);

  const { done, position } = useMemo(() => flightAt(planned, progress), [planned, progress]);
  const track = useMemo(() => (planned.length ? { planned, sprayed: done, drone: position } : undefined), [planned, done, position]);

  const features = useMemo<MapFeature[]>(
    () => (lote ? [{ id: lote.loteId, name: lote.name, boundary: lote.boundary, kind: 'lote' as const, latitude: lote.latitude, longitude: lote.longitude }] : []),
    [lote],
  );

  const totals = useMemo(() => ({
    count: flights.length,
    ha: flights.reduce((a, f) => a + (f.areaCoveredHa ?? 0), 0),
    liters: flights.reduce((a, f) => a + (f.litersApplied ?? 0), 0),
    hours: flights.reduce((a, f) => a + (f.durationSec ?? 0), 0) / 3600,
  }), [flights]);

  if (flightsQ.isLoading || lotesQ.isLoading || modulesQ.isLoading) return <Spinner label={t.common.loading} />;

  const battery = selected?.telemetry
    ? Math.round(selected.telemetry.startBattery - progress * (selected.telemetry.startBattery - selected.telemetry.endBattery))
    : undefined;

  return (
    <div className={s.root}>
      <PageHeader title={t.drones.title} subtitle={t.drones.subtitle} actions={canImport ? <Button onClick={() => setImportOpen(true)}>{t.droneImport.button}</Button> : undefined} />

      <SearchableSelect
        label={t.drones.zoneFilter}
        placeholder={t.common.searchLotePlaceholder}
        options={zoneOptions}
        value={zoneFilter}
        onChange={setZoneFilter}
        noResultsText={t.common.searchNoResults}
      />

      <div className={s.kpis}>
        <KpiCard label={t.drones.flights} value={fmtNum(totals.count)} />
        <KpiCard label={t.drones.hectares} value={fmtNum(totals.ha, { maximumFractionDigits: 1 })} />
        <KpiCard label={t.drones.liters} value={fmtNum(totals.liters, { maximumFractionDigits: 0 })} />
        <KpiCard label={t.drones.flightHours} value={fmtNum(totals.hours, { maximumFractionDigits: 1 })} />
      </div>

      {flights.length === 0 ? (
        <div className={s.note}>{t.drones.empty}</div>
      ) : (
        <div className={s.body}>
          <div className={s.panel}>
            <Text weight="semibold">
              {t.drones.replay}{lote ? ` · ${lote.name}` : ''}{selected ? ` · ${t.drones.flightOf} ${fmtDateTime(selected.flightDate)}` : ''}
            </Text>
            <div className={s.map}>
              <MapView features={features} satellite fitKey={selected?.loteId} track={track} style={{ height: '100%', width: '100%' }} />
            </div>
            <div className={s.bar}><div className={s.barFill} style={{ width: `${progress * 100}%` }} /></div>
            <div className={s.controls}>
              <Button icon={playing ? <Pause24Regular /> : <Play24Regular />} onClick={() => { if (progress >= 1) setProgress(0); setPlaying(p => !p); }}>
                {playing ? t.drones.pause : t.drones.play}
              </Button>
              <Button icon={<ArrowCounterclockwise24Regular />} appearance="subtle" onClick={() => { setProgress(0); setPlaying(true); }}>{t.drones.restart}</Button>
              <Text size={200}>{t.drones.speed}</Text>
              <Dropdown
                value={`×${speed}`} selectedOptions={[String(speed)]} style={{ minWidth: 90 }}
                onOptionSelect={(_, d) => setSpeed(Number(d.optionValue))}
              >
                {SPEEDS.map(v => <Option key={v} value={String(v)} text={`×${v}`}>{`×${v}`}</Option>)}
              </Dropdown>
            </div>
            {selected && (
              <div className={s.stats}>
                <span>{t.drones.elapsed}: <b>{mmss(progress * (selected.durationSec ?? 0))}</b></span>
                <span>{t.drones.sprayed}: <b>{fmtNum(progress * (selected.areaCoveredHa ?? 0), { maximumFractionDigits: 2 })} ha</b> · <b>{fmtNum(progress * (selected.litersApplied ?? 0), { maximumFractionDigits: 1 })} L</b></span>
                {battery !== undefined && <span>{t.drones.battery}: <b>{battery}%</b></span>}
              </div>
            )}
            <div className={s.note}>{t.drones.simulated}</div>
          </div>

          <div className={s.tableBox}>
            <DataTable<DroneFlight>
              rows={flights}
              rowKey={(f) => f.flightId}
              onRowClick={(f) => setSelectedId(f.flightId)}
              isRowSelected={(f) => f.flightId === selected?.flightId}
              columns={[
                { key: 'date', header: t.drones.colDate, width: 1.4, render: (f) => fmtDateTime(f.flightDate) },
                { key: 'lote', header: t.drones.colLote, render: (f) => loteById.get(f.loteId)?.name ?? f.loteId },
                { key: 'ha', header: t.drones.colHa, align: 'right', render: (f) => fmtNum(f.areaCoveredHa ?? 0, { maximumFractionDigits: 2 }) },
                { key: 'liters', header: t.drones.colLiters, align: 'right', render: (f) => fmtNum(f.litersApplied ?? 0, { maximumFractionDigits: 1 }) },
                { key: 'dur', header: t.drones.colDuration, align: 'right', render: (f) => mmss(f.durationSec ?? 0) },
              ]}
            />
          </div>
        </div>
      )}

      <DroneFlightImportDialog open={importOpen} onClose={() => setImportOpen(false)} />
    </div>
  );
}

export default DronesPage;
